"use client";

import React from 'react';
import { useCheckoutSuccess } from '../hooks/useCheckoutFlow';

export interface CheckoutSuccessProps {
  onSuccess?: (sessionId: string, planId: string | null) => void;
  redirectUrl?: string;
  redirectDelay?: number;
  className?: string;
  showConfetti?: boolean;
}

export function CheckoutSuccess({
  onSuccess,
  redirectUrl,
  redirectDelay = 3000,
  className = "",
  showConfetti = true
}: CheckoutSuccessProps) {
  const { sessionId, planId, isSuccess } = useCheckoutSuccess();

  React.useEffect(() => {
    if (isSuccess && sessionId) {
      onSuccess?.(sessionId, planId);
      
      if (redirectUrl) {
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, redirectDelay);
      }
    }
  }, [isSuccess, sessionId, planId, onSuccess, redirectUrl, redirectDelay]);

  if (!isSuccess) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${className}`}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Processing payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-center min-h-screen ${className}`}>
      <div className="text-center max-w-md">
        {showConfetti && (
          <div className="text-6xl mb-6">🎉</div>
        )}
        
        <svg
          className="mx-auto h-16 w-16 text-green-500 mb-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        
        <h1 className="text-3xl font-bold mb-4">Payment Successful!</h1>
        
        <p className="text-lg text-muted-foreground mb-6">
          Thank you for your purchase. Your subscription is now active.
        </p>
        
        {planId && (
          <p className="text-sm text-muted-foreground mb-4">
            Plan ID: <span className="font-mono">{planId}</span>
          </p>
        )}
        
        {redirectUrl && (
          <p className="text-sm text-muted-foreground">
            Redirecting in {Math.ceil(redirectDelay / 1000)} seconds...
          </p>
        )}
        
        <div className="mt-8">
          <button
            onClick={() => window.location.href = redirectUrl || '/'}
            className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition"
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}